import { useMemo, useState } from "react";
import styled from "styled-components";
import {
  BanknotesIcon,
  LockClosedIcon,
  PuzzlePieceIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@sablier/v2-components/molecules";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { FEATURE_EMPTY_PROXY_ENABLED } from "~/client/constants";
import { useAccount } from "~/client/hooks";
import type { ComponentProps } from "react";
import Balances from "./Balances";
import Configure from "./Configure";
import Plugin from "./Plugin";

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.container}
  ${(props) => props.theme.styles.row}
  & {
    flex: 1;
    flex-shrink: 0;
    align-items: flex-start;
    width: 100%;
    padding: 0;
    border-top: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Left = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    justify-content: flex-start;
    & > * {
      justify-content: flex-start;
      width: 100%;
      height: auto;
      padding-block: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
      padding-inline: calc(${(props) => props.theme.sizes.modalEdge} * 1);
      &:not(:last-child) {
        border-bottom: 2px solid ${(props) => props.theme.colors.border};
      }
    }
  }
`;

const Right = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex: 1;
    align-items: flex-start;
    min-height: 540px;
    padding: calc(${(props) => props.theme.sizes.modalEdge});
    border-left: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    ${(props) => props.theme.styles.column}
    & {
      ${Left} {
        ${(props) => props.theme.styles.row}
        & {
          width: 100%;
          border-bottom: 2px solid ${(props) => props.theme.colors.border};
          & > * {
            justify-content: center;
            border: none;
            &:not(:last-child) {
              border-right: 2px solid ${(props) => props.theme.colors.border};
            }
          }
        }
      }
      ${Right} {
        min-height: 440px;
        padding: calc(${(props) => props.theme.sizes.edge} * 2);
        padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
        border: none;
      }
    }
  }

  ${(props) => props.theme.medias.maxSM} {
    ${Right} {
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
      padding-inline: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    }
    ${Left} {
      display: flex;
      & > * {
        flex: auto;
      }
      & > *:not([data-preview="true"]) {
        flex: 1;
        *[data-component="title"] {
          display: none;
        }
      }
    }
  }

  ${(props) => props.theme.medias.max(270)} {
    ${Left} {
      display: flex;
      & > * {
        flex: 1;
        *[data-component="title"] {
          display: none;
        }
      }
    }
  }
`;

interface Item {
  active: ComponentProps<typeof Button>;
  locked?: ComponentProps<typeof Button>;
  inactive: ComponentProps<typeof Button>;
}

type Tab = "balances" | "configure" | "plugin";

function Body() {
  const { proxy } = useAccount();
  const { t } = useT();
  const [tab, setTab] = useState<Tab>("configure");
  const items = useMemo(() => {
    return {
      balances: {
        active: {
          accent: "dark200",
          appearance: "solid",
          isPreview: true,
          left: BanknotesIcon,
          title: _.capitalize(t("words.balances")),
        },
        inactive: {
          accent: "dark1000",
          appearance: "transparent",
          left: BanknotesIcon,
          title: _.capitalize(t("words.balances")),
          onClick: () => setTab("balances"),
        },
      },
      configure: {
        active: {
          accent: "dark200",
          appearance: "solid",
          left: ShieldCheckIcon,
          isPreview: true,
          title: _.capitalize(t("words.configure")),
        },
        inactive: {
          accent: "dark1000",
          appearance: "transparent",
          left: ShieldCheckIcon,
          title: _.capitalize(t("words.configure")),
          onClick: () => setTab("configure"),
        },
      },
      plugin: {
        active: {
          accent: "dark200",
          appearance: "solid",
          left: PuzzlePieceIcon,
          isPreview: true,
          title: _.capitalize(t("words.plugin")),
        },
        inactive: {
          accent: "dark1000",
          appearance: "transparent",
          left: PuzzlePieceIcon,
          title: _.capitalize(t("words.plugin")),
          onClick: () => setTab("plugin"),
        },
      },
    } satisfies Record<Tab, Item>;
  }, [t, setTab]);

  return (
    <Wrapper>
      <Left>
        <Button
          {...items.configure[tab === "configure" ? "active" : "inactive"]}
        />
        <Button
          {...items.balances[tab === "balances" ? "active" : "inactive"]}
          {...(_.isNilOrEmptyString(proxy) || !FEATURE_EMPTY_PROXY_ENABLED
            ? { left: LockClosedIcon }
            : {})}
        />
        <Button
          {...items.plugin[tab === "plugin" ? "active" : "inactive"]}
          {...(_.isNilOrEmptyString(proxy) || !FEATURE_EMPTY_PROXY_ENABLED
            ? { left: LockClosedIcon }
            : {})}
        />
      </Left>
      <Right>
        {tab === "configure" && <Configure />}
        {tab === "balances" && <Balances />}
        {tab === "plugin" && <Plugin />}
      </Right>
    </Wrapper>
  );
}

export default Body;
